import React, { useState } from 'react';
import { 
  Music, 
  Send, 
  Plus, 
  X, 
  CheckCircle,
  AlertCircle,
  Upload,
  Link as LinkIcon,
  Instagram,
  Globe,
  Moon
} from 'lucide-react';
import { api } from '../utils/api';

const ArtistApplyPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    genres: [],
    bio: '',
    mixLinks: [''],
    availability: [],
    website: '',
    instagram: '',
    experience: '',
    equipment: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const genreOptions = ['Minimal', 'Tech House', 'Deep House', 'Progressive', 'Techno', 'Melodic', 'Ambient'];
  const availabilityOptions = ['Warm-up', 'Peak Time', 'Closing', 'After-hours', 'Open Air', 'Special Events'];

  const handleGenreToggle = (genre) => {
    setFormData(prev => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter(g => g !== genre)
        : [...prev.genres, genre]
    }));
  };

  const handleAvailabilityToggle = (option) => {
    setFormData(prev => ({
      ...prev,
      availability: prev.availability.includes(option)
        ? prev.availability.filter(a => a !== option)
        : [...prev.availability, option]
    }));
  };

  const handleAddMixLink = () => {
    setFormData(prev => ({
      ...prev,
      mixLinks: [...prev.mixLinks, '']
    }));
  };

  const handleRemoveMixLink = (index) => {
    setFormData(prev => ({
      ...prev,
      mixLinks: prev.mixLinks.filter((_, i) => i !== index)
    }));
  };

  const handleMixLinkChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      mixLinks: prev.mixLinks.map((link, i) => i === index ? value : link)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      // Filter out empty mix links
      const cleanedData = {
        ...formData,
        mixLinks: formData.mixLinks.filter(link => link.trim()),
        social: {
          instagram: formData.instagram
        }
      };

      await api.artists.apply(cleanedData);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting application:', error);
      setError('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="p-4 bg-[#0A0A0A] rounded-full w-20 h-20 mx-auto flex items-center justify-center">
            <CheckCircle className="text-green-500" size={40} />
          </div>
          <h1 className="text-3xl font-light">Application Submitted!</h1>
          <p className="text-[#888888]">
            Thank you for your interest in joining after.am. We'll review your application 
            and get back to you within 48 hours.
          </p>
          <a href="/" className="btn-primary inline-flex items-center gap-2">
            Return Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center space-y-4 mb-12">
          <div className="flex justify-center mb-6">
            <Moon className="text-[#D4A574]" size={32} />
          </div>
          <h2 className="text-2xl font-light">Artist Application</h2>
          <p className="text-[#888888]">
            Apply to play at after.am events
          </p>
        </div>

        {/* Application Form */}
        <form onSubmit={handleSubmit} className="card-dark p-8 space-y-8">
          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-center gap-2">
              <AlertCircle size={20} className="text-red-500" />
              <p className="text-red-500">{error}</p>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-6">
            <h3 className="text-xl font-light flex items-center gap-2">
              <Music className="text-[#D4A574]" size={24} />
              Basic Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#888888] mb-2">
                  Artist Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="input-dark w-full"
                  placeholder="Your artist name"
                />
              </div>
              
              <div>
                <label className="block text-sm text-[#888888] mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="input-dark w-full"
                  placeholder="your@email.com"
                />
              </div>
              
              <div>
                <label className="block text-sm text-[#888888] mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="input-dark w-full"
                  placeholder="+351 xxx xxx xxx"
                />
              </div>
              
              <div>
                <label className="block text-sm text-[#888888] mb-2">
                  Instagram
                </label>
                <div className="relative">
                  <Instagram className="absolute left-3 top-3 text-[#888888]" size={20} />
                  <input
                    type="text"
                    value={formData.instagram}
                    onChange={(e) => setFormData({...formData, instagram: e.target.value})}
                    className="input-dark w-full pl-10"
                    placeholder="@username"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-[#888888] mb-2">
                Website
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-3 text-[#888888]" size={20} />
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  className="input-dark w-full pl-10"
                  placeholder="https://your-website.com"
                />
              </div>
            </div>
          </div>

          {/* Musical Style */}
          <div className="space-y-4">
            <h3 className="text-xl font-light">Musical Style</h3>
            
            <div>
              <label className="block text-sm text-[#888888] mb-3">
                Select your genres *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {genreOptions.map(genre => (
                  <button
                    key={genre}
                    type="button"
                    onClick={() => handleGenreToggle(genre)}
                    className={`p-3 rounded-lg border transition-all ${
                      formData.genres.includes(genre)
                        ? 'border-[#D4A574] bg-[#D4A574]/10 text-[#D4A574]'
                        : 'border-[#333333] hover:border-[#888888]'
                    }`}
                  >
                    {genre}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-[#888888] mb-2">
                Bio / About You *
              </label>
              <textarea
                required
                value={formData.bio}
                onChange={(e) => setFormData({...formData, bio: e.target.value})}
                rows={4}
                className="input-dark w-full"
                placeholder="Tell us about your musical journey, influences, and style..."
              />
            </div>
          </div>

          {/* Mix Links */}
          <div className="space-y-4">
            <h3 className="text-xl font-light">Mix Links</h3>
            <p className="text-sm text-[#888888]">Share your recent mixes or tracks</p>
            
            {formData.mixLinks.map((link, index) => (
              <div key={index} className="flex gap-2">
                <div className="relative flex-1">
                  <LinkIcon className="absolute left-3 top-3 text-[#888888]" size={20} />
                  <input
                    type="url"
                    value={link}
                    onChange={(e) => handleMixLinkChange(index, e.target.value)}
                    className="input-dark w-full pl-10"
                    placeholder="https://soundcloud.com/..."
                  />
                </div>
                {formData.mixLinks.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveMixLink(index)}
                    className="p-3 hover:bg-[#1A1A1A] rounded-lg transition-colors"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            ))}
            
            <button
              type="button"
              onClick={handleAddMixLink}
              className="btn-secondary flex items-center gap-2"
            >
              <Plus size={20} />
              Add Another Link
            </button>
          </div>

          {/* Availability */}
          <div className="space-y-4">
            <h3 className="text-xl font-light">Availability</h3>
            <label className="block text-sm text-[#888888] mb-3">
              What type of sets are you available for?
            </label>
            <div className="grid grid-cols-2 gap-3">
              {availabilityOptions.map(option => (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleAvailabilityToggle(option)}
                  className={`p-3 rounded-lg border transition-all ${
                    formData.availability.includes(option)
                      ? 'border-[#D4A574] bg-[#D4A574]/10 text-[#D4A574]'
                      : 'border-[#333333] hover:border-[#888888]'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {/* Experience */}
          <div className="space-y-4">
            <h3 className="text-xl font-light">Experience & Equipment</h3>
            
            <div>
              <label className="block text-sm text-[#888888] mb-2">
                Playing Experience
              </label>
              <textarea
                value={formData.experience}
                onChange={(e) => setFormData({...formData, experience: e.target.value})}
                rows={3}
                className="input-dark w-full"
                placeholder="Tell us about venues you've played, residencies, or notable events..."
              />
            </div>
            
            <div>
              <label className="block text-sm text-[#888888] mb-2">
                Equipment / Technical Rider
              </label>
              <textarea
                value={formData.equipment}
                onChange={(e) => setFormData({...formData, equipment: e.target.value})}
                rows={3}
                className="input-dark w-full"
                placeholder="CDJs, controllers, vinyl setup, etc..."
              />
            </div>
          </div>

          {/* Submit */}
          <div className="pt-6 border-t border-[#333333]">
            <button
              type="submit"
              disabled={submitting || formData.genres.length === 0}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Submit Application
                </>
              )}
            </button>
          </div>
        </form>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-[#888888]">
          <p>By submitting this application, you agree to our selection process.</p>
          <p className="mt-2">
            Questions? Contact us at{' '}
            <a href="mailto:artists@after.am" className="text-[#D4A574] hover:underline">
              artists@after.am
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ArtistApplyPage;